import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StringParam, useQueryParam } from 'use-query-params'

import { useBreadcrumbs } from '../../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'

import { Flex, Tab, Tabs } from '@percent/lemonade'
import { POSTHOG_FEATURE_FLAGS, usePostHogFeatureFlag } from '@percent/partner-dashboard/common/hooks'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { ActionsTab } from './ActionsTab/ActionsTab'
import { CampaignsTab } from './CampaignsTab/CampaignsTab'
import { GroupsTab } from './GroupsTab/GroupsTab'

enum CampaignsHomeTabs {
  CAMPAIGNS = 'campaigns',
  GROUPS = 'groups',
  ACTIONS = 'actions'
}

export function CampaignsHome() {
  const { t } = useTranslation()
  const givingCampaignGroupsFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_GIVING_CAMPAIGN_GROUPS)
  const givingActionsFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_GIVING_ACTIONS)
  const { userCan } = usePermissionCheck()
  const canViewGivingActions = givingActionsFeatureFlag && userCan('viewGivingActions')
  const [tabQP, setTabQP] = useQueryParam('tab', StringParam)

  useBreadcrumbs([
    {
      label: t('breadcrumb.campaigns')
    }
  ])

  const tabs = useMemo(
    () => [
      { label: t('typography.campaigns'), key: CampaignsHomeTabs.CAMPAIGNS },
      ...(givingCampaignGroupsFeatureFlag ? [{ label: t('typography.groups'), key: CampaignsHomeTabs.GROUPS }] : []),
      ...(canViewGivingActions ? [{ label: t('typography.actions'), key: CampaignsHomeTabs.ACTIONS }] : [])
    ],
    [t, givingCampaignGroupsFeatureFlag, canViewGivingActions]
  )

  const activeTab = useMemo(() => {
    if (!tabQP) return tabs[0].key

    return tabs.find(tab => tab.key === tabQP)?.key ?? tabs[0].key
  }, [tabs, tabQP])

  if (tabs.length === 1) {
    return <CampaignsTab />
  }

  return (
    <Flex direction="column" bg="white" style={{ borderRadius: '8px' }}>
      <Tabs items={tabs} activeTab={activeTab} onChangeTab={setTabQP} />
      <Tab index={CampaignsHomeTabs.CAMPAIGNS} value={activeTab}>
        <CampaignsTab />
      </Tab>
      {givingCampaignGroupsFeatureFlag && (
        <Tab index={CampaignsHomeTabs.GROUPS} value={activeTab}>
          <GroupsTab />
        </Tab>
      )}
      {canViewGivingActions && (
        <Tab index={CampaignsHomeTabs.ACTIONS} value={activeTab}>
          <ActionsTab />
        </Tab>
      )}
    </Flex>
  )
}
